import type { PcmAudio } from "../types";

type AudioContextCtor = typeof AudioContext;
type OfflineAudioContextCtor = typeof OfflineAudioContext;

type DecodeAudioContext = AudioContext | OfflineAudioContext;

function resolveAudioContext(): AudioContextCtor | null {
  const ctor =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  return ctor ?? null;
}

function resolveOfflineAudioContext(): OfflineAudioContextCtor | null {
  const ctor =
    globalThis.OfflineAudioContext ??
    (globalThis as { webkitOfflineAudioContext?: OfflineAudioContextCtor })
      .webkitOfflineAudioContext;
  return ctor ?? null;
}

function createDecodeAudioContext(): DecodeAudioContext {
  const AudioCtx = resolveAudioContext();
  if (AudioCtx) return new AudioCtx();

  const OfflineCtx = resolveOfflineAudioContext();
  if (OfflineCtx) {
    return new OfflineCtx({ numberOfChannels: 1, length: 1, sampleRate: 44100 });
  }

  throw new Error("Web Audio API is not available in this environment");
}

/** Copy an `AudioBuffer`'s samples into our plain {@link PcmAudio} form. */
export function fromAudioBuffer(buffer: AudioBuffer): PcmAudio {
  const channelData: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    // getChannelData returns a live view; copy so the buffer can be GC'd.
    channelData.push(buffer.getChannelData(c).slice());
  }
  return { sampleRate: buffer.sampleRate, channelData };
}

/**
 * Decode any container/codec the browser natively supports (mp3, wav, m4a/aac,
 * ogg, flac, opus, ...) into {@link PcmAudio}.
 *
 * Browser-only: uses `AudioContext.decodeAudioData`. Throws a clear error if the
 * browser cannot decode the given bytes (unsupported codec) — callers surface
 * that to the user.
 */
export async function decodeAudioFile(input: Blob | ArrayBuffer): Promise<PcmAudio> {
  const arrayBuffer = input instanceof ArrayBuffer ? input : await input.arrayBuffer();
  const ctx = createDecodeAudioContext();
  try {
    // decodeAudioData copies the input; the slice guards Safari's detaching.
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
    return fromAudioBuffer(audioBuffer);
  } catch (cause) {
    throw new Error("Could not decode this audio file — the format may be unsupported", { cause });
  } finally {
    if ("close" in ctx) await ctx.close();
  }
}
